"use client"

import React, { useEffect, useState, useRef, memo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import firebase from "firebase/compat/app"
import "firebase/compat/firestore"
import logo from "./logo.png"
import brand from "./logo-two.png"
import "react-toastify/dist/ReactToastify.css"
import { ToastContainer, toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faVideo,
  faMicrophone,
  faMicrophoneSlash,
  faVideoCamera,
  faVideoSlash,
  faPhoneSlash,
  faStethoscope,
  faExpand,
  faCompress,
  faUser,
  faClock,
  faWifi,
  faBars,
  faTimes,
  faUserMd,
  faHeart,
} from "@fortawesome/free-solid-svg-icons"
import "./CallPage.css"
import "./feedback.css"

// VideoPreview Component
const VideoPreview = memo(({ stream }) => {
  const videoRef = useRef(null)

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream
    }
  }, [stream])

  return <video ref={videoRef} className="preview-video" autoPlay playsInline />
})

// Enhanced Modal Component
const Modal = ({ show, onClose, setHideBackground, onNameSubmit }) => {
  const [inputValue, setInputValue] = useState("")
  const [localStream, setLocalStream] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let stream = null
    const getUserMedia = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        })
        setLocalStream(stream)
      } catch (error) {
        console.error("Error accessing media devices:", error)
        toast.error("Camera access required for video consultation")
      }
    }

    if (show) {
      getUserMedia()
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
      setLocalStream(null)
    }
  }, [show])

  const handleContinue = async () => {
    if (inputValue.trim()) {
      setIsLoading(true)
      try {
        await onNameSubmit(inputValue)
        setHideBackground(true)
        onClose()
      } catch (error) {
        console.error("Error submitting name:", error)
        toast.error("Failed to join waiting room. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && inputValue.trim() && !isLoading) {
      handleContinue()
    }
  }

  return show ? (
    <div className="enhanced-modal-overlay">
      <div className="enhanced-modal-container">
        <div className="enhanced-modal-content">
          {/* Header Section */}
          <div className="enhanced-modal-header">
            <div className="header-decoration">
              <div className="decoration-circle circle-1"></div>
              <div className="decoration-circle circle-2"></div>
              <div className="decoration-circle circle-3"></div>
            </div>
            <div className="modal-brand">
              <img src={brand || "/placeholder.svg"} alt="Logo" className="brand-logo-two" />
            </div>
            <div className="welcome-content">
              <div className="welcome-icon">
                <FontAwesomeIcon icon={faUserMd} />
              </div>
              <h1 className="welcome-title">Welcome eHEALTH JAMAICA Meeting Portal</h1>
              <p className="welcome-subtitle">
                For quality assurance, documentation, and service improvement purposes, your interaction may be recorded or transcribed by an AI-powered assistant. This includes voice or text communications during your consultation or use of our digital services.
                By continuing, you acknowledge and consent to this process. All data is handled in accordance with our privacy policy and applicable data protection laws.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="enhanced-modal-body">
            <div className="consultation-layout">
              {/* Left Side - Form */}
              <div className="consultation-form">
                <div className="form-section">
                  <div className="input-group-two">
                    <label htmlFor="patientName" className="input-label">
                      <FontAwesomeIcon icon={faUser} className="label-icon" />
                      Your Full Name
                    </label>
                    <input
                      id="patientName"
                      className="enhanced-input"
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter your full name..."
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>

                  <div className="action-section">
                    <button className="join-button" onClick={handleContinue} disabled={!inputValue.trim() || isLoading}>
                      {isLoading ? (
                        <>
                          <div className="button-spinner"></div>
                          <span>Joining Waiting Room...</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faHeart} className="button-icon" />
                          <span>Join Waiting Room</span>
                        </>
                      )}
                    </button>

                    <div className="security-note">
                      <div className="security-icon">🔒</div>
                      <span>Your consultation is secure and private</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Video Preview */}
              <div className="consultation-preview">
                <div className="preview-section">
                  <div className="preview-header">
                    <h3>Camera Preview</h3>
                    <div className="preview-status">
                      <div className="status-dot active"></div>
                      <span>Camera Ready</span>
                    </div>
                  </div>
                  <div className="enhanced-preview-container">
                    {localStream ? (
                      <VideoPreview stream={localStream} />
                    ) : (
                      <div className="preview-placeholder">
                        <FontAwesomeIcon icon={faVideoCamera} className="placeholder-icon" />
                        <p>Initializing camera...</p>
                      </div>
                    )}
                    <div className="preview-overlay">
                      <div className="preview-label">This is how you'll appear to your doctor</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="enhanced-modal-footer">
            <div className="footer-info">
              <div className="info-item">
                <FontAwesomeIcon icon={faClock} />
                <span>Average wait time: 2-5 minutes</span>
              </div>
              <div className="info-item">
                <FontAwesomeIcon icon={faWifi} />
                <span>Secure encrypted connection</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null
}

// FeedbackForm Component
const FeedbackForm = ({ onSubmit, callId }) => {
  const [platformRating, setPlatformRating] = useState(0)
  const [doctorRating, setDoctorRating] = useState(0)
  const [recommendation, setRecommendation] = useState(null)
  const [comments, setComments] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hoveredEmoji, setHoveredEmoji] = useState(null)

  const emojis = ["😔", "☹️", "😐", "🙂", "😁"]
  const emojiLabels = ["Very Bad", "Bad", "Neutral", "Good", "Very Good"]
  const maxRating = 5

  const handlePlatformRating = (value) => {
    setPlatformRating(value)
  }

  const handleDoctorRating = (value) => {
    setDoctorRating(value)
  }

  const handleRecommendation = (value) => {
    setRecommendation(value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!platformRating && !doctorRating && recommendation === null && !comments.trim()) {
      toast.error("Please provide at least one rating or comment before submitting.")
      return
    }

    setIsSubmitting(true)
    const link = `${window.location.origin}/call/${callId}`
    const smiley_rating = recommendation !== null ? recommendation + 1 : 0

    const payload = {
      link,
      star_rating: platformRating,
      smiley_rating,
      doctor_rating: doctorRating,
      comments,
    }

    const token = localStorage.getItem("token") || "your-token-here"

    try {
      const response = await fetch(`${process.env.REACT_APP_ORIGIN_URL}/patient-rating`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        console.log("Feedback submitted successfully")
        toast.success("Thank you for your feedback!")
        onSubmit()
      } else {
        const errorText = await response.text()
        console.error("Error submitting feedback:", response.statusText, errorText)
        toast.error(`Failed to submit feedback: ${errorText || response.statusText}. Please try again.`)
      }
    } catch (error) {
      console.error("Error submitting feedback:", error)
      toast.error("An error occurred while submitting feedback. Please check your connection and try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    onSubmit()
  }

  return (
    <div className="bg-image">
      <div className="form-container">
        <div className="logo mb-4 text-center">
          <img src={logo || "/placeholder.svg"} alt="eHealth Logo" style={{ maxHeight: "200px", width: "auto" }} />
        </div>
        <h3>We Value Your Feedback</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">How would you describe your experience with our platform?</label>
            <div className="rate">
              {[...Array(maxRating)].map((_, index) => {
                const ratingValue = maxRating - index
                return (
                  <React.Fragment key={ratingValue}>
                    <input
                      type="radio"
                      id={`star${ratingValue}`}
                      name="platform-rate"
                      value={ratingValue}
                      checked={platformRating === ratingValue}
                      onChange={() => handlePlatformRating(ratingValue)}
                    />
                    <label htmlFor={`star${ratingValue}`} />
                  </React.Fragment>
                )
              })}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">What rating would you give to the doctor?</label>
            <div className="ratings">
              {[...Array(maxRating)].map((_, index) => {
                const ratingValue = index + 1
                return (
                  <div
                    key={ratingValue}
                    className={`stethoscope ${doctorRating >= ratingValue ? "selected" : ""}`}
                    onClick={() => handleDoctorRating(ratingValue)}
                  >
                    <FontAwesomeIcon icon={faStethoscope} />
                  </div>
                )
              })}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Would you recommend our service to your family and friends?</label>
            <div className="emojis">
              {emojis.map((emoji, index) => (
                <div
                  key={index}
                  className={`emoji-wrapper ${recommendation === index ? "selected" : ""}`}
                  onClick={() => handleRecommendation(index)}
                  onMouseEnter={() => setHoveredEmoji(index)}
                  onMouseLeave={() => setHoveredEmoji(null)}
                >
                  <span className="emoji">{emoji}</span>
                  {hoveredEmoji === index && <span className="emoji-tooltip">{emojiLabels[index]}</span>}
                </div>
              ))}
            </div>
            {recommendation !== null && (
              <p className="selected-label">
                Selected: <span>{emojiLabels[recommendation]}</span>
              </p>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">Do you have any suggestions or additional comments?</label>
            <textarea
              className="form-control"
              rows="4"
              placeholder="Add your comments..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button type="button" className="btn-cancel" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting && <span className="spinner-border" role="status" aria-hidden="true"></span>}
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Main CallPage Component
const CallPage = () => {
  const { id } = useParams()
  const [localStream, setLocalStream] = useState(null)
  const [remoteStream, setRemoteStream] = useState(null)
  const [pc, setPC] = useState(null)
  const [initialized, setInitialized] = useState(false)
  const [callEnded, setCallEnded] = useState(false)
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)
  const [showModal, setShowModal] = useState(true)
  const [hideBackground, setHideBackground] = useState(false)
  const [isMicOn, setIsMicOn] = useState(true)
  const [isVideoOn, setIsVideoOn] = useState(true)
  const [isRemoteVideoVisible, setIsRemoteVideoVisible] = useState(false)
  const [callStatus, setCallStatus] = useState("pending")
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [controlsTimeout, setControlsTimeout] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [patientName, setPatientName] = useState("")
  const [isLinkExpired, setIsLinkExpired] = useState(false)

  const videoContainerRef = useRef(null)
  const navigate = useNavigate()

  // Check call status on mount
  useEffect(() => {
    const initializeFirebaseAndCheckStatus = async () => {
      const firebaseConfig = {
        apiKey: "AIzaSyBDXL7mezxWb1nEjYpTmiDnmsS-7GYykLo",
        authDomain: "ejamica-call.firebaseapp.com",
        databaseURL: "https://ejamica-call-default-rtdb.firebaseio.com",
        projectId: "ejamica-call",
        storageBucket: "ejamica-call.firebasestorage.app",
        messagingSenderId: "56137661542",
        appId: "1:56137661542:web:190294cc6da5fce7f5c1a7",
      }
      if (!firebase.apps.length) {
        firebase.initializeApp(firebaseConfig)
      }
      setInitialized(true)

      // Check call status
      const firestore = firebase.firestore()
      const callDoc = firestore.collection("calls").doc(id)
      const doc = await callDoc.get()
      if (doc.exists) {
        const callData = doc.data()
        if (callData.status === "ended") {
          setIsLinkExpired(true)
          setShowModal(false)
          setShowFeedbackForm(false)
        }
      } else {
        setIsLinkExpired(true)
        setShowModal(false)
        setShowFeedbackForm(false)
      }
    }

    initializeFirebaseAndCheckStatus()
  }, [id])

  // Auto-hide controls in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      const hideControls = () => {
        setShowControls(false)
      }

      const showControlsTemporarily = () => {
        setShowControls(true)
        if (controlsTimeout) clearTimeout(controlsTimeout)
        const timeout = setTimeout(hideControls, 3000)
        setControlsTimeout(timeout)
      }

      const handleMouseMove = () => showControlsTemporarily()

      document.addEventListener("mousemove", handleMouseMove)
      showControlsTemporarily()

      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        if (controlsTimeout) clearTimeout(controlsTimeout)
      }
    } else {
      setShowControls(true)
      if (controlsTimeout) clearTimeout(controlsTimeout)
    }
  }, [isFullscreen, controlsTimeout])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoContainerRef.current?.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  useEffect(() => {
    const initializeCall = async () => {
      if (isLinkExpired) return

      const firestore = firebase.firestore()
      const callDoc = firestore.collection("calls").doc(id)
      const answerCandidates = callDoc.collection("answerCandidates")
      const offerCandidates = callDoc.collection("offerCandidates")

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
          },
        })
        setLocalStream(stream)
        console.log("Participant: Local stream initialized")

        const newPC = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun1.l.google.com:19302" },
            { urls: "stun:stun2.l.google.com:19302" },
            {
              urls: "turn:turn.example.com:3478",
              username: "your-username",
              credential: "your-password",
            },
          ],
          iceCandidatePoolSize: 10,
        })

        newPC.ontrack = (event) => {
          if (event.streams && event.streams[0]) {
            console.log("Participant: Remote stream received from host")
            setRemoteStream(event.streams[0])
          }
        }

        newPC.oniceconnectionstatechange = () => {
          console.log("Participant: ICE connection state:", newPC.iceConnectionState)
          if (newPC.iceConnectionState === "failed") {
            console.error("Participant: ICE connection failed")
            cleanupCall()
          }
        }

        stream.getTracks().forEach((track) => {
          newPC.addTrack(track, stream)
          console.log("Participant: Added track to PeerConnection:", track)
        })

        newPC.onicecandidate = (event) => {
          if (event.candidate) {
            answerCandidates.add(event.candidate.toJSON())
            console.log("Participant: Added answer ICE candidate:", event.candidate)
          }
        }

        const callData = (await callDoc.get()).data()
        const offerDescription = callData ? callData.offer : null

        if (offerDescription) {
          await newPC.setRemoteDescription(new RTCSessionDescription(offerDescription))
          console.log("Participant: Remote offer set")

          const answerDescription = await newPC.createAnswer()
          await newPC.setLocalDescription(answerDescription)
          console.log("Participant: Answer created and set locally")

          const answer = {
            type: answerDescription.type,
            sdp: answerDescription.sdp,
          }

          await callDoc.update({ answer })

          offerCandidates.onSnapshot((snapshot) => {
            snapshot.docChanges().forEach((change) => {
              if (change.type === "added") {
                const candidate = new RTCIceCandidate(change.doc.data())
                newPC
                  .addIceCandidate(candidate)
                  .then(() => console.log("Participant: Added offer ICE candidate"))
                  .catch((error) => console.error("Participant: Error adding offer ICE candidate:", error))
              }
            })
          })

          setPC(newPC)
        }

        const unsubscribe = callDoc.onSnapshot((docSnapshot) => {
          const callData = docSnapshot.data()
          if (callData) {
            setCallStatus(callData.status)
            if (callData.status === "accepted") {
              setIsRemoteVideoVisible(true)
            } else if (callData.status === "ended" && !callEnded) {
              cleanupCall()
            }
          }
        })

        return () => unsubscribe()
      } catch (error) {
        console.error("Participant: Error initializing call:", error)
        toast.error("Failed to access camera/microphone. Please check permissions.")
      }
    }

    if (initialized && !isLinkExpired) {
      initializeCall()
    }
  }, [id, initialized, callEnded, isLinkExpired])

  useEffect(() => {
    if (initialized && !isLinkExpired) {
      const firestore = firebase.firestore()
      const callDoc = firestore.collection("calls").doc(id)

      const unsubscribe = callDoc.onSnapshot((docSnapshot) => {
        const callData = docSnapshot.data()
        if (callData) {
          setCallStatus(callData.status)
          if (callData.status === "accepted") {
            setIsRemoteVideoVisible(true)
          } else if (callData.status === "ended" && !callEnded) {
            cleanupCall()
          } else if (callData.status === "declined") {
            setCallEnded(true)
            setShowFeedbackForm(false)
            toast.info("Call declined by the doctor.")
          }
        }
      })

      return () => unsubscribe()
    }
  }, [id, initialized, callEnded, isLinkExpired])

  const cleanupCall = () => {
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop())
      setLocalStream(null)
    }
    if (remoteStream) {
      setRemoteStream(null)
    }
    if (pc) {
      pc.close()
      setPC(null)
    }
    setCallEnded(true)
    setIsRemoteVideoVisible(false)
    setShowFeedbackForm(true)
    setIsFullscreen(false)
  }

  const handleHangUp = async () => {
    try {
      const firestore = firebase.firestore()
      const callDoc = firestore.collection("calls").doc(id)
      await callDoc.update({
        status: "ended",
        endedAt: firebase.firestore.FieldValue.serverTimestamp(),
      })
      console.log("Call status updated to ended.")
    } catch (error) {
      console.error("Error updating call status:", error)
    }
    cleanupCall()
  }

  const toggleMicrophone = () => {
    if (localStream) {
      localStream.getAudioTracks()[0].enabled = !isMicOn
      setIsMicOn(!isMicOn)
      toast.info(isMicOn ? "Microphone muted" : "Microphone unmuted")
    }
  }

  const toggleCamera = () => {
    if (localStream) {
      localStream.getVideoTracks()[0].enabled = !isVideoOn
      setIsVideoOn(!isVideoOn)
      toast.info(isVideoOn ? "Camera turned off" : "Camera turned on")
    }
  }

  const handleNameSubmit = async (name) => {
    try {
      setPatientName(name)
      const firestore = firebase.firestore()
      const callDoc = firestore.collection("calls").doc(id)

      await callDoc.update({
        participantName: name,
        status: "pending",
        joinedAt: firebase.firestore.FieldValue.serverTimestamp(),
      })

      console.log("Participant name and status updated to pending.")
      setShowModal(false)
      setHideBackground(true)
      toast.success("You've joined the waiting room!")
    } catch (error) {
      console.error("Error updating Firestore:", error)
      toast.error("Failed to join waiting room. Please try again.")
    }
  }

  const handleFeedbackSubmit = () => {
    setShowFeedbackForm(false)
    navigate("/")
  }

  const getStatusColor = () => {
    switch (callStatus) {
      case "pending":
        return "#ffc107"
      case "accepted":
        return "#28a745"
      case "ended":
        return "#6c757d"
      case "declined":
        return "#dc3545"
      default:
        return "#6c757d"
    }
  }

  const getStatusText = () => {
    switch (callStatus) {
      case "pending":
        return "Waiting for doctor..."
      case "accepted":
        return "In consultation"
      case "ended":
        return "Call ended"
      case "declined":
        return "Call declined"
      default:
        return "Connecting..."
    }
  }

  if (isLinkExpired) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="card text-center shadow-lg" style={{ maxWidth: "500px" }}>
          <div className="card-body">
            <h5 className="card-title">Link Expired</h5>
            <p className="card-text">
              Your link is expired! If you have any query, please contact{" "}
              <a href="mailto:info@ehealthjm.com">info@ehealthjm.com</a>.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (callEnded && !showFeedbackForm) {
    return (
      <div className="call-ended-container">
        <div className="call-ended-content">
          <h3>Call Ended</h3>
          <p>Thank you for using our service. Redirecting to feedback form...</p>
          <div className="loading-spinner"></div>
        </div>
      </div>
    )
  }

  if (showFeedbackForm) {
    return (
      <>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <FeedbackForm onSubmit={handleFeedbackSubmit} callId={id} />
      </>
    )
  }

  return (
    <div className="call-page">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        setHideBackground={setHideBackground}
        onNameSubmit={handleNameSubmit}
      />

      <div className="call-layout">
        <button className="mobile-menu-btn" onClick={toggleSidebar}>
          <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />
        </button>

        {sidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

        <div className={`call-sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
          <div className="sidebar-header">
            <div className="logo-wrapper">
              <img className="logo-img" src={brand || "/placeholder.svg"} alt="Logo" />
            </div>
          </div>

          <div className="sidebar-content">
            <div className="sidebar-section">
              <h6 className="sidebar-title">
                <FontAwesomeIcon icon={faUser} className="sidebar-icon" />
                <span className="sidebar-text">PATIENT INFO</span>
              </h6>
              <div className="patient-info-card">
                <div className="patient-avatar">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="patient-details">
                  <div className="patient-name">{patientName || "Patient"}</div>
                  <div className="patient-status">
                    <span className="status-dot" style={{ backgroundColor: getStatusColor() }}></span>
                    <span className="status-text">{getStatusText()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="sidebar-section">
              <h6 className="sidebar-title">
                <FontAwesomeIcon icon={faWifi} className="sidebar-icon" />
                <span className="sidebar-text">CONNECTION</span>
              </h6>
              <div className="connection-info">
                <div className="connection-item">
                  <FontAwesomeIcon icon={faClock} className="connection-icon" />
                  <span className="connection-text">Connected</span>
                </div>
              </div>
            </div>

            <div className="sidebar-section">
              <h6 className="sidebar-title">
                <span className="sidebar-text">GENERAL</span>
              </h6>
              <div className="sidebar-menu">
                <div className="sidebar-item active">
                  <FontAwesomeIcon icon={faVideo} className="sidebar-icon" />
                  <span className="sidebar-text">Video Call</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="call-main">
          {!isRemoteVideoVisible && !showModal && localStream && (
            <div className="waiting-room">
              <div className="waiting-content">
                <div className="waiting-header">
                  <h2>Waiting for Doctor</h2>
                  <p>Please wait while the doctor joins the consultation</p>
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <div className="waiting-video">
                  <video
                    ref={(ref) => {
                      if (ref) {
                        ref.srcObject = localStream
                      }
                    }}
                    className="waiting-video-element"
                    autoPlay
                    muted
                    playsInline
                  />
                  <div className="video-label">Your Video Preview</div>
                </div>
              </div>
            </div>
          )}

          {isRemoteVideoVisible && (
            <div className={`video-call-container ${isFullscreen ? "fullscreen" : ""}`} ref={videoContainerRef}>
              <div className="remote-video-container">
                <video
                  ref={(ref) => {
                    if (ref) {
                      ref.srcObject = remoteStream
                    }
                  }}
                  className="remote-video"
                  autoPlay
                  playsInline
                />

                <div className="local-video-pip">
                  <video
                    ref={(ref) => {
                      if (ref) {
                        ref.srcObject = localStream
                      }
                    }}
                    className="local-video"
                    autoPlay
                    muted
                    playsInline
                  />
                  <div className="pip-label">You</div>
                </div>

                <div className={`video-controls ${showControls ? "visible" : "visible"}`}>
                  <div className="controls-group">
                    <button
                      className={`control-btn ${isMicOn ? "active" : "inactive"}`}
                      onClick={toggleMicrophone}
                      title={isMicOn ? "Mute" : "Unmute"}
                    >
                      <FontAwesomeIcon icon={isMicOn ? faMicrophone : faMicrophoneSlash} />
                    </button>

                    <button
                      className={`control-btn ${isVideoOn ? "active" : "inactive"}`}
                      onClick={toggleCamera}
                      title={isVideoOn ? "Turn off camera" : "Turn on camera"}
                    >
                      <FontAwesomeIcon icon={isVideoOn ? faVideoCamera : faVideoSlash} />
                    </button>

                    <button className="control-btn end-call" onClick={handleHangUp} title="End call">
                      <FontAwesomeIcon icon={faPhoneSlash} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CallPage